import React from "react";
import "./SocialPostCard.css";

export interface SocialPostCardProps {
  platform: "mastodon" | "pixelfed" | "loops";
  postUrl: string;
  content?: string; // HTML string for Mastodon
  imageUrl?: string | null; // Pixelfed image or Loops avatar
  caption?: string; // Text content
  date?: string;
  loading: boolean;
  error: string | null;
}

const PLATFORM_NAMES = {
  mastodon: "Mastodon",
  pixelfed: "Pixelfed",
  loops: "Loops",
};

export function SocialPostCard({
  platform,
  postUrl,
  content,
  imageUrl,
  caption,
  date,
  loading,
  error,
}: SocialPostCardProps) {
  if (loading) {
    return (
      <div className={`social-card skeleton ${platform}`}>
        <div className="skeleton-header"></div>
        <div className="skeleton-content"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`social-card error ${platform}`}>
        <p>Failed to load {PLATFORM_NAMES[platform]} post.</p>
        <a href={postUrl} target="_blank" rel="noopener noreferrer">
          View Profile
        </a>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    // If the user clicked an anchor tag or video controls inside the card, let the browser handle it
    const target = e.target as HTMLElement;
    if (target.closest("a") || target.closest("video") || target.closest("audio") || target.closest("button")) {
      return;
    }
    window.open(postUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      onClick={handleCardClick}
      className={`social-card ${platform}`}
      style={{ cursor: "pointer" }}
    >
      <div className="card-header">
        <a 
          href={postUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="platform-name"
          style={{ color: "var(--accent-color)", textDecoration: "none" }}
        >
          {PLATFORM_NAMES[platform]}
        </a>
        {date && (
          <span className="post-date">
            {new Date(date).toLocaleDateString("fi-FI")}
          </span>
        )}
      </div>

      <div className="card-body">
        {imageUrl && (
          <div
            className={`card-image ${platform === "loops" ? "avatar" : ""}`}
            style={{ backgroundImage: `url(${imageUrl})` }}
          ></div>
        )}
        
        {content && (
          <div
            className="card-content html-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
        
        {caption && !content && <p className="card-content">{caption}</p>}
      </div>
    </div>
  );
}
